import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Droplet, Heart, Brain, Ribbon, ArrowRight, Loader2 } from "lucide-react";
import AIDigestCard from "./AIDigestCard";
import CrossSpecialtyInsight from "./CrossSpecialtyInsight";

/**
 * Cross-Specialty Command Center hero row.
 *
 * Fetches `/api/platform/command-center` and renders one tile per specialty
 * for which the scoped patient has data. Multi-morbid patients (like Sarah
 * Chen — CKD G4 + severe HTN + epilepsy + breast Ca) see the WHOLE picture
 * the moment they log in — no clicking through consoles required.
 */

const STATUS_COLORS = {
  ok:        { color: "#4ADE80", bg: "rgba(74, 222, 128, 0.10)", border: "rgba(74, 222, 128, 0.35)" },
  attention: { color: "#FBBF24", bg: "rgba(251, 191, 36, 0.10)", border: "rgba(251, 191, 36, 0.35)" },
  warning:   { color: "#FB923C", bg: "rgba(251, 146, 60, 0.12)", border: "rgba(251, 146, 60, 0.35)" },
  critical:  { color: "#F87171", bg: "rgba(248, 113, 113, 0.12)", border: "rgba(248, 113, 113, 0.40)" },
};

const SPECIALTY_ICON = {
  nephrology: Droplet,
  cardiology: Heart,
  neurology:  Brain,
  oncology:   Ribbon,
};

const SPECIALTY_ACCENT = {
  nephrology: "#4ADE80",
  cardiology: "#F87171",
  neurology:  "#A78BFA",
  oncology:   "#FBBF24",
};

export default function CommandCenter() {
  const [tiles, setTiles] = useState(null);   // null = loading, [] = no data
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/command-center");
      setTiles(data.tiles || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load command center");
      setTiles([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (tiles === null) {
    return (
      <div className="ros-card flex items-center gap-2 text-[#A3ACA7] text-sm"
           data-testid="command-center-loading">
        <Loader2 size={14} className="animate-spin" /> Assembling your multi-specialty view…
      </div>
    );
  }

  if (tiles.length === 0) {
    // Silent when no data — dashboard has its own empty states
    return null;
  }

  return (
    <div className="space-y-3" data-testid="command-center">
      <CrossSpecialtyInsight />

      <div className="flex items-baseline justify-between">
        <div>
          <div className="ros-label" style={{ fontSize: 10 }}>Command Center</div>
          <div className="font-heading text-lg text-white mt-0.5">Multi-specialty snapshot</div>
        </div>
        <div className="text-xs text-[#A3ACA7]">
          {tiles.length} active {tiles.length === 1 ? "specialty" : "specialties"}
        </div>
      </div>

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${Math.min(tiles.length, 4)}, minmax(0, 1fr))` }}
        data-testid="command-center-tiles"
      >
        {tiles.map((tile, idx) => (
          <CommandTile key={tile.specialty}
                       tile={tile}
                       index={idx}
                       onClick={() => navigate(tile.route)} />
        ))}
      </div>

      <AIDigestCard activeSpecialties={tiles.map((t) => t.specialty)} />

      {error && (
        <div className="text-xs text-[#F87171]" data-testid="command-center-error">{error}</div>
      )}
    </div>
  );
}

function CommandTile({ tile, index, onClick }) {
  const Icon = SPECIALTY_ICON[tile.specialty] || Droplet;
  const accent = SPECIALTY_ACCENT[tile.specialty] || "#A3ACA7";
  const s = STATUS_COLORS[tile.status] || STATUS_COLORS.ok;

  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06 }}
      onClick={onClick}
      data-testid={`command-tile-${tile.specialty}`}
      className="ros-card text-left transition-all hover:-translate-y-0.5"
      style={{ borderColor: s.border, background: s.bg }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center"
               style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
            <Icon size={16} style={{ color: accent }} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">{tile.label}</div>
            <div className="text-[10px] font-semibold" style={{ color: accent }}>
              {tile.primary_metric}
            </div>
          </div>
        </div>
        <ArrowRight size={14} className="opacity-40" />
      </div>

      <div className="mt-3">
        <div className="text-3xl font-heading font-bold text-white leading-tight">
          {tile.value}
          {tile.unit && (
            <span className="text-sm text-[#A3ACA7] font-normal ml-1">{tile.unit}</span>
          )}
        </div>
        {tile.secondary && (
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
              {tile.secondary_label}
            </span>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ color: s.color, background: `${s.color}18`, border: `1px solid ${s.color}40` }}
              data-testid={`command-tile-${tile.specialty}-status`}
            >
              {tile.secondary}
            </span>
          </div>
        )}
      </div>
    </motion.button>
  );
}
